// A null conversationId is the unsaved New chat tab.
export interface TreadAgentTab {
  conversationId: string | null;
}

export const MAX_OPEN_TREAD_AGENT_TABS = 8;

const DRAFT_TAB: TreadAgentTab = { conversationId: null };
const OPEN_TABS_STORAGE_KEY_PREFIX = 'tread.tread_agent.open_tabs';

function isDraftTab(tab: TreadAgentTab) {
  return tab.conversationId === null;
}

function capTabs(tabs: TreadAgentTab[]) {
  return tabs.slice(-MAX_OPEN_TREAD_AGENT_TABS);
}

export function addConversationTab(tabs: TreadAgentTab[], conversationId: string) {
  if (tabs.some((tab) => tab.conversationId === conversationId)) {
    return tabs;
  }

  return capTabs([...tabs, { conversationId }]);
}

export function addDraftTab(tabs: TreadAgentTab[]) {
  return tabs.some(isDraftTab) ? tabs : capTabs([...tabs, DRAFT_TAB]);
}

export function reconcileTabs(
  tabs: TreadAgentTab[],
  activeConversationId: string | null,
  isPanelOpen: boolean,
) {
  if (activeConversationId) {
    if (tabs.some((tab) => tab.conversationId === activeConversationId)) {
      return tabs;
    }

    if (tabs.some(isDraftTab)) {
      return tabs.map((tab) =>
        isDraftTab(tab) ? { conversationId: activeConversationId } : tab,
      );
    }

    return addConversationTab(tabs, activeConversationId);
  }

  return isPanelOpen ? addDraftTab(tabs) : tabs;
}

function openTabsStorageKey(companyId: string) {
  return `${OPEN_TABS_STORAGE_KEY_PREFIX}.${companyId}`;
}

export function readStoredTabs(companyId: string): TreadAgentTab[] {
  try {
    const parsed: unknown = JSON.parse(
      localStorage.getItem(openTabsStorageKey(companyId)) ?? 'null',
    );

    if (!Array.isArray(parsed)) {
      return [];
    }

    const conversationIds = parsed.filter(
      (value): value is string => typeof value === 'string',
    );

    return capTabs(conversationIds.map((conversationId) => ({ conversationId })));
  } catch {
    return [];
  }
}

export function writeStoredTabs(companyId: string, tabs: TreadAgentTab[]) {
  const conversationIds = tabs.flatMap((tab) =>
    tab.conversationId ? [tab.conversationId] : [],
  );

  try {
    localStorage.setItem(openTabsStorageKey(companyId), JSON.stringify(conversationIds));
  } catch {
    // Storage is unavailable in private browsing; the tabs still hold for this session.
  }
}
